import React, { useState } from "react"

import { trpc } from "../../lib/trpc"

interface FilterOptions {
  sourceType?: "news" | "x"
  sourceId?: string
  searchQuery?: string
  dateFrom?: string
  dateTo?: string
}

const ContentViewer: React.FC = () => {
  const [filters, setFilters] = useState<FilterOptions>({})
  const [limit] = useState(20)
  const [offset, setOffset] = useState(0)

  // Queries
  const { data: content, refetch } = trpc.contentIngestion.getContent.useQuery({
    ...filters,
    dateFrom: filters.dateFrom ? new Date(filters.dateFrom) : undefined,
    dateTo: filters.dateTo ? new Date(filters.dateTo) : undefined,
    limit,
    offset,
  })

  const { data: sources } = trpc.contentSources.listSources.useQuery({
    limit: 100,
  })

  const { data: stats } = trpc.contentIngestion.getIngestionStats.useQuery({
    sourceId: filters.sourceId,
    dateFrom: filters.dateFrom ? new Date(filters.dateFrom) : undefined,
    dateTo: filters.dateTo ? new Date(filters.dateTo) : undefined,
  })

  const handleFilterChange = (newFilters: Partial<FilterOptions>) => {
    setFilters({ ...filters, ...newFilters })
    setOffset(0) // Reset pagination when filters change
  }

  const handleNextPage = () => {
    setOffset(offset + limit)
  }

  const handlePrevPage = () => {
    setOffset(Math.max(0, offset - limit))
  }

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString()
  }

  const truncateContent = (content: string, maxLength = 200) => {
    if (content.length <= maxLength) return content
    return content.substring(0, maxLength) + "..."
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">Content Viewer</h1>
        <p className="">Browse and search ingested content from all sources</p>

        {stats && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="text-sm ">Total Items</div>
              <div className="text-xl font-semibold">{stats.totalItems}</div>
            </div>
            <div className="bg-orange-50 p-3 rounded-lg">
              <div className="text-sm ">News Items</div>
              <div className="text-xl font-semibold">{stats.newsItems}</div>
            </div>
            <div className="bg-purple-50 p-3 rounded-lg">
              <div className="text-sm ">X Posts</div>
              <div className="text-xl font-semibold">{stats.xItems}</div>
            </div>
            <div className="bg-green-50 p-3 rounded-lg">
              <div className="text-sm ">Today</div>
              <div className="text-xl font-semibold">{stats.todayItems}</div>
            </div>
            <div className="bg-cyan-50 p-3 rounded-lg">
              <div className="text-sm ">This Week</div>
              <div className="text-xl font-semibold">{stats.thisWeekItems}</div>
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="mb-6 bg-foregroundx p-4 rounded-lg">
        <h3 className="font-medium mb-3">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Content Type</label>
            <select
              value={filters.sourceType || ""}
              onChange={(e) =>
                handleFilterChange({
                  sourceType: (e.target.value as "news" | "x") || undefined,
                })
              }
              className="w-full border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">All Types</option>
              <option value="news">News</option>
              <option value="x">X/Twitter</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Source</label>
            <select
              value={filters.sourceId || ""}
              onChange={(e) => handleFilterChange({ sourceId: e.target.value || undefined })}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">All Sources</option>
              {sources?.sources.map((source) => (
                <option key={source.id} value={source.id}>
                  {source.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">From Date</label>
            <input
              type="date"
              value={filters.dateFrom || ""}
              onChange={(e) => handleFilterChange({ dateFrom: e.target.value || undefined })}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">To Date</label>
            <input
              type="date"
              value={filters.dateTo || ""}
              onChange={(e) => handleFilterChange({ dateTo: e.target.value || undefined })}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Search</label>
            <input
              type="text"
              value={filters.searchQuery || ""}
              onChange={(e) => handleFilterChange({ searchQuery: e.target.value || undefined })}
              placeholder="Search content..."
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>
        </div>

        <div className="mt-3 flex space-x-2">
          <button
            onClick={() => {
              setFilters({})
              setOffset(0)
            }}
            className="px-3 py-1 bg-foregroundx0 text-white text-sm rounded hover:bg-gray-600"
          >
            Clear Filters
          </button>
          <button
            onClick={() => refetch()}
            className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Content List */}
      <div className="space-y-4">
        {content?.items.map((item) => (
          <div key={item.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${
                      item.sourceType === "news" ? "bg-orange-100 text-orange-800" : "bg-purple-100 text-purple-800"
                    }`}
                  >
                    {item.sourceType === "news" ? "News" : "X/Twitter"}
                  </span>
                  <span className="text-sm ">{item.sourceName}</span>
                  <span className="text-xs text-gray-500">{formatDate(item.publishedAt.toString())}</span>
                </div>

                {item.title && <h3 className="font-semibold text-lg mb-2 leading-tight">{item.title}</h3>}

                <p className="text-primary mb-3 leading-relaxed">{truncateContent(item.content)}</p>

                {/* Metadata */}
                <div className="text-xs text-gray-500 space-y-1">
                  {item.sourceType === "x" && item.metadata && (
                    <div className="flex space-x-4">
                      <span>❤️ {item.metadata.likes || 0}</span>
                      <span>🔄 {item.metadata.retweets || 0}</span>
                      <span>💬 {item.metadata.replies || 0}</span>
                      {item.metadata.author && <span>by @{item.metadata.author.username}</span>}
                    </div>
                  )}
                  {item.sourceType === "news" && item.metadata && (
                    <div className="flex space-x-4">
                      {item.metadata.author && <span>by {item.metadata.author}</span>}
                      {item.metadata.tags && item.metadata.tags.length > 0 && (
                        <span>Tags: {item.metadata.tags.join(", ")}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="ml-4 flex flex-col space-y-2">
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                >
                  View Original
                </a>
                <div className="text-xs text-gray-500 text-right">
                  Ingested: {formatDate(item.createdAt.toString())}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {content && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm ">
            Showing {offset + 1}-{Math.min(offset + limit, content.total)} of {content.total} items
          </div>

          <div className="flex space-x-2">
            <button
              onClick={handlePrevPage}
              disabled={offset === 0}
              className="px-3 py-1 bg-foregroundx0 text-white text-sm rounded hover:bg-gray-600 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={handleNextPage}
              disabled={!content.hasMore}
              className="px-3 py-1 bg-foregroundx0 text-white text-sm rounded hover:bg-gray-600 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {content?.items.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <div className="text-lg mb-2">No content found</div>
          <div className="text-sm">Try adjusting your filters or add some content sources</div>
        </div>
      )}
    </div>
  )
}

export default ContentViewer
